import { useState } from 'react';
import { Plus, FileText, Pencil, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useMediaProposals,
  useCreateProposal,
  useUpdateProposal,
  useDeleteProposal,
  useUploadMediaImage,
  useUploadMediaFile,
  type MediaProposal,
} from '@/lib/api/hooks/use-media';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

export function ProposalsTab() {
  const [search, setSearch] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MediaProposal | null>(null);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnailUrl: '',
    fileUrl: '',
    fileSize: '',
    date: new Date().toISOString().split('T')[0],
  });

  const filters = {
    search: search || undefined,
  };

  const { data: items = [], isLoading } = useMediaProposals(filters);
  const createItem = useCreateProposal();
  const updateItem = useUpdateProposal();
  const deleteItem = useDeleteProposal();
  const uploadImage = useUploadMediaImage();
  const uploadFile = useUploadMediaFile();

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingThumbnail(true);
    try {
      const result = await uploadImage.mutateAsync({ file, folder: 'media/proposals' });
      setFormData((prev) => ({ ...prev, thumbnailUrl: result.url }));
    } finally {
      setIsUploadingThumbnail(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingFile(true);
    try {
      const result = await uploadFile.mutateAsync({ file, folder: 'media/proposals' });
      setFormData((prev) => ({
        ...prev,
        fileUrl: result.url,
        fileSize: result.size
      }));
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleOpenDialog = (item?: MediaProposal) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        title: item.title,
        description: item.description,
        thumbnailUrl: item.thumbnailUrl || '',
        fileUrl: item.fileUrl,
        fileSize: item.fileSize || '',
        date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
    } else {
      setSelectedItem(null);
      setFormData({
        title: '',
        description: '',
        thumbnailUrl: '',
        fileUrl: '',
        fileSize: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedItem) {
      await updateItem.mutateAsync({ id: selectedItem.id, data: formData });
    } else {
      await createItem.mutateAsync(formData);
    }

    setIsSheetOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    await deleteItem.mutateAsync(selectedItem.id);
    setIsDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  if (isLoading) {
    return <div className="text-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Input
          placeholder="Search proposals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:max-w-xs"
        />
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Proposal
        </Button>
      </div>

      {/* Proposals Grid */}
      {items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No proposals yet"
          description="Start adding project proposals and documents."
          action={{
            label: 'Add Proposal',
            onClick: () => handleOpenDialog(),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-lg overflow-hidden group hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[3/2] relative overflow-hidden bg-muted">
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground mb-2 line-clamp-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {item.description}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>{format(new Date(item.date), 'MMM dd, yyyy')}</span>
                  {item.fileSize && <span>{item.fileSize}</span>}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(item.fileUrl, '_blank')}
                    className="flex-1"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsDeleteDialogOpen(true);
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              {selectedItem ? 'Edit Proposal' : 'Add Proposal'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground min-h-[120px]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">PDF File *</label>
              <div className="space-y-2">
                {formData.fileUrl && (
                  <div className="p-3 bg-muted rounded-md flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">PDF Uploaded</p>
                        {formData.fileSize && (
                          <p className="text-xs text-muted-foreground">{formData.fileSize}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(formData.fileUrl, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={isUploadingFile}
                />
                {isUploadingFile && (
                  <p className="text-sm text-muted-foreground">Uploading PDF...</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail (Optional)</label>
              <div className="space-y-2">
                {formData.thumbnailUrl && (
                  <div className="aspect-[3/2] relative overflow-hidden rounded-md border border-border">
                    <img
                      src={formData.thumbnailUrl}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={isUploadingThumbnail}
                />
                {isUploadingThumbnail && (
                  <p className="text-sm text-muted-foreground">Uploading thumbnail...</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Date *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setIsSheetOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!formData.fileUrl || createItem.isPending || updateItem.isPending}>
                {selectedItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Proposal"
        description="Are you sure you want to delete this proposal? This action cannot be undone."
      />
    </div>
  );
}
